import { useEffect, useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';

import useAuth from '@/hooks/useAuth';

import { itemByLanguage } from '@/utils/language';

import PathwayModal from '@/components/popups/PathwayModal';

import Constants from '@/constants';

import {
  PrintReportConfigureStep,
  PrintReportLoadingStep,
  PrintReportReadyStep,
} from './PrintReportSteps';
import { closePrintReportAtom, printReportAtom, setShowDoBAtom } from './state';
import { usePrintReportData } from './usePrintReportData';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';

/**
 * Unified PrintReport component that handles printing for:
 * - Plan reports (compliance and non-compliance)
 * - Team member summary reports
 *
 * Reads from printReportAtom for what to print.
 * Mount this once at the app level (Layout.tsx).
 */
const PrintReport: React.FC = () => {
  const track = useAmplitudeTrack();
  const { data: auth } = useAuth();

  const isAuthLoaded = !!auth?.userId;
  const locationsWithAtLeastLeaderRole =
    auth?.allLocationsWithAtLeastLeaderRoles ?? [];

  const { subject, showDoB } = useAtomValue(printReportAtom);
  const setShowDoB = useSetAtom(setShowDoBAtom);
  const closePrintReport = useSetAtom(closePrintReportAtom);

  const isOpen = subject !== null;
  const isPlanPrint = subject?.type === 'plan';
  const isTeamMemberPrint = subject?.type === 'teamMember';
  const isCompliance = isPlanPrint && subject.plan.type === 'COMPLIANCE';

  // Modal state
  const [printStep, setPrintStep] = useState(0);
  const [noLocationsSelected, setNoLocationsSelected] = useState(false);
  const [isLoadedWithMinTime, setIsLoadedWithMinTime] = useState(false);
  const [userSelectedLocations, setUserSelectedLocations] = useState<
    string[] | null
  >(null);

  // Fetch report data (lazy - call startFetching to trigger)
  const { printLocations, isFetching, generateReport, startFetching } =
    usePrintReportData({ subject });

  // Final selected locations (user selection or defaults)
  const defaultLocations = isPlanPrint
    ? isCompliance
      ? locationsWithAtLeastLeaderRole
      : printLocations
    : locationsWithAtLeastLeaderRole;
  const selectedLocations = userSelectedLocations ?? defaultLocations;
  const locationOptions = isPlanPrint
    ? printLocations
    : isTeamMemberPrint
      ? []
      : locationsWithAtLeastLeaderRole;

  const isFoodSafety =
    isPlanPrint &&
    itemByLanguage(subject.plan.name)?.includes(
      Constants.FOOD_SAFETY_COURSE_NAME,
    );

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    setPrintStep(0);
    setNoLocationsSelected(false);
    setIsLoadedWithMinTime(false);
    setUserSelectedLocations(null);
  }

  const effectivePrintStep =
    printStep === 1 && isLoadedWithMinTime && !isFetching ? 2 : printStep;

  // Minimum loading time to avoid flash
  useEffect(() => {
    if (printStep === 1) {
      const timer = setTimeout(() => setIsLoadedWithMinTime(true), 1200);
      return () => clearTimeout(timer);
    }
  }, [printStep]);

  const handleClose = () => {
    closePrintReport();
  };

  const handleCreatePdf = () => {
    if (!selectedLocations.length && locationOptions.length) {
      setNoLocationsSelected(true);
    } else {
      startFetching(selectedLocations);
      setPrintStep(1);
    }
  };

  const handlePrint = () => {
    track(AmplitudeProductEvent.REPORT_PRINT, {
      report_type: subject?.type ?? 'unknown',
      plan_id: isPlanPrint ? subject.plan.id : undefined,
      plan_name: isPlanPrint ? itemByLanguage(subject.plan.name) : undefined,
      team_member_id: isTeamMemberPrint ? subject.id : undefined,
      is_native: window.native.isNative,
    });
    generateReport(showDoB, selectedLocations);
    handleClose();
  };

  const stepKeys = ['configure', 'loading', 'ready'] as const;
  const stepKey = stepKeys[effectivePrintStep];

  return (
    <PathwayModal
      isOpen={isAuthLoaded && isOpen}
      onClose={handleClose}
      stepKey={stepKey}
      size="sm"
      className="max-w-[480px]!"
      showCloseButton
    >
      {effectivePrintStep === 0 && (
        <PrintReportConfigureStep
          selectedLocations={selectedLocations}
          locationOptions={locationOptions}
          onLocationsChange={setUserSelectedLocations}
          noLocationsSelected={noLocationsSelected}
          isFoodSafety={isFoodSafety ?? false}
          showDoB={showDoB}
          onShowDoBChange={setShowDoB}
          onClose={handleClose}
          onCreatePdf={handleCreatePdf}
        />
      )}
      {effectivePrintStep === 1 && <PrintReportLoadingStep />}
      {effectivePrintStep === 2 && (
        <PrintReportReadyStep onPrint={handlePrint} />
      )}
    </PathwayModal>
  );
};

export default PrintReport;
